import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, btn, input, Field, Modal, EmptyState, StatusBadge } from "@/components/kit";
import ProgressRing from "@/components/ProgressRing";
import { toast } from "sonner";
import { Plus, Users, Link2, Search, Download } from "lucide-react";

export default function Clients() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [flows, setFlows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", company: "", flow_id: "" });

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/flows").then((r) => { setFlows(r.data); }).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!f.flow_id) { toast.error("Select an onboarding flow"); return; }
    try {
      const { data } = await api.post("/clients", f);
      toast.success("Client added");
      setOpen(false);
      setF({ name: "", email: "", company: "", flow_id: "" });
      nav(`/app/clients/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const copyLink = (c) => {
    const url = `${window.location.origin}/onboard/${c.invite_token}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Invite link copied")).catch(() => toast.message(url));
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/export/clients.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "clients.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = clients.filter((c) =>
    [c.name, c.email, c.company, c.flow_name].join(" ").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Clients" subtitle="Everyone you're onboarding, in one place.">
        <button className={btn.outline} onClick={exportCsv} data-testid="export-csv-button"><Download className="h-4 w-4" /> Export CSV</button>
        <button className={btn.primary} onClick={() => setOpen(true)} data-testid="add-client-button"><Plus className="h-4 w-4" /> New client</button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" data-testid="client-search" className={`${input} pl-9`} />
      </div>

      {clients.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" subtitle="Add your first client and send them a branded onboarding link.">
          <button className={btn.primary} onClick={() => setOpen(true)} data-testid="empty-add-client"><Plus className="h-4 w-4" /> Add client</button>
        </EmptyState>
      ) : (
        <div className="bg-card border border-border overflow-x-auto thin-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-6 py-3 font-medium">Client</th>
                <th className="px-6 py-3 font-medium">Flow</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Progress</th>
                <th className="px-6 py-3 font-medium text-right">Invite</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-secondary/40 transition-colors cursor-pointer" onClick={() => nav(`/app/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <td className="px-6 py-4"><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.company || c.email}</p></td>
                  <td className="px-6 py-4 text-muted-foreground">{c.flow_name}</td>
                  <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                  <td className="px-6 py-4"><div className="flex items-center gap-2"><ProgressRing value={c.progress} size={32} stroke={4} label={false} /><span className="tabular-nums text-xs">{c.progress}%</span></div></td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={(e) => { e.stopPropagation(); copyLink(c); }} className={btn.ghost} data-testid={`copy-invite-${c.id}`}><Link2 className="h-4 w-4" /> Copy link</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add client" testid="add-client-modal">
        <form onSubmit={create} className="space-y-4">
          <Field label="Full name"><input required className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="client-name-input" /></Field>
          <Field label="Email"><input type="email" required className={input} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="client-email-input" /></Field>
          <Field label="Company (optional)"><input className={input} value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="client-company-input" /></Field>
          <Field label="Onboarding flow" hint={flows.length === 0 ? "Create a flow first from the Flows page." : ""}>
            <select required className={input} value={f.flow_id} onChange={(e) => setF({ ...f, flow_id: e.target.value })} data-testid="client-flow-select">
              <option value="">Select a flow…</option>
              {flows.map((fl) => <option key={fl.id} value={fl.id}>{fl.name}</option>)}
            </select>
          </Field>
          <button type="submit" className={`${btn.primary} w-full`} data-testid="client-submit">Add client & open</button>
        </form>
      </Modal>
    </div>
  );
}
